import React, { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  TextField,
  Typography,
} from "@mui/material";
import { getExportFilterOptions } from "./exportApi";
import {
  ALL_OPTION_VALUE,
  dropdownOptions,
  isFilterLocked,
  pickedFromAutocomplete,
  pruneDependents,
  visibleOptions,
  withAllOption,
} from "./filterOptions";
import type { ExportFilter, ExportFilterOption } from "./types";

type Props = {
  reportType: string;
  params: Record<string, string | undefined>;
  filters: ExportFilter[];
  value: Record<string, string[]>;
  onChange: (next: Record<string, string[]>) => void;
};

const FEMININE_FILTER_NOUNS = new Set(["marcas", "categorías", "categorias", "industrias", "ubicaciones"]);

/** "Todas" for feminine plural labels (Marcas, Categorías), "Todos" otherwise. */
export function allLabel(filterLabel: string): string {
  const first = (filterLabel.trim().split(/\s+/)[0] ?? "").toLowerCase();
  return FEMININE_FILTER_NOUNS.has(first) ? "Todas" : "Todos";
}

/** Cached per report and params; shared by the filter inputs and the dialog summary. */
export function useExportFilterOptions(
  reportType: string,
  params: Record<string, string | undefined>,
  enabled = true,
) {
  return useQuery({
    queryKey: ["export-filter-options", reportType, params],
    queryFn: () => getExportFilterOptions(reportType, params),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

const ExportFiltersSection: React.FC<Props> = ({ reportType, params, filters, value, onChange }) => {
  const optionsQuery = useExportFilterOptions(reportType, params);
  const options = optionsQuery.data;

  const byValue = useMemo(() => {
    const maps: Record<string, Map<string, ExportFilterOption>> = {};
    for (const f of filters) {
      maps[f.key] = new Map((options?.[f.key] ?? []).map((o) => [o.value, o]));
    }
    return maps;
  }, [filters, options]);

  // A new period reloads the options; drop selections that no longer exist in it.
  useEffect(() => {
    if (!options) return;
    let changed = false;
    const next: Record<string, string[]> = {};
    for (const [key, values] of Object.entries(value)) {
      const known = byValue[key];
      next[key] = known ? values.filter((v) => known.has(v)) : values;
      if (next[key]!.length !== values.length) changed = true;
    }
    if (changed) onChange(pruneDependents(filters, options, next));
  }, [options, byValue, value, filters, onChange]);

  const gridSx = {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      md: `repeat(${Math.min(Math.max(filters.length, 1), 3)}, minmax(0, 1fr))`,
    },
    gap: 1.5,
  } as const;

  if (optionsQuery.isError) {
    return (
      <Alert severity="warning">
        No se pudieron cargar los filtros. Puedes exportar sin filtrar.
      </Alert>
    );
  }

  const loading = !options;

  return (
    <Box sx={gridSx}>
      {filters.map((f) => {
        const available = options ? visibleOptions(f, options, value) : [];
        const locked = isFilterLocked(f, value);
        const requiredLabel = filters.find((r) => r.key === f.requires)?.label.toLowerCase() ?? "";
        const selected = (value[f.key] ?? [])
          .map((v) => byValue[f.key]?.get(v) ?? (loading ? { value: v, label: "Cargando…" } : undefined))
          .filter((o): o is ExportFilterOption => o != null);
        const allOption: ExportFilterOption = {
          value: ALL_OPTION_VALUE,
          label: loading ? allLabel(f.label) : `${allLabel(f.label)} (${available.length})`,
        };
        const shown = locked ? [] : withAllOption(selected, allOption);
        return (
          <Autocomplete
            key={f.key}
            multiple
            size="small"
            disabled={locked}
            disableCloseOnSelect
            limitTags={3}
            options={loading ? [] : [allOption, ...available]}
            filterOptions={(opts, state) => dropdownOptions(opts, state.inputValue)}
            loading={loading}
            loadingText="Cargando opciones…"
            value={shown}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            getOptionLabel={(o) => o.label}
            onChange={(_e, picked) => {
              const next = { ...value, [f.key]: pickedFromAutocomplete(shown, picked) };
              onChange(options ? pruneDependents(filters, options, next) : next);
            }}
            renderTags={(tags, getTagProps) =>
              tags.map((option, index) => {
                const { key, onDelete, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    size="small"
                    label={option.label}
                    {...tagProps}
                    {...(option.value === ALL_OPTION_VALUE ? {} : { onDelete })}
                  />
                );
              })
            }
            renderOption={(props, option, { selected: isSelected }) => {
              const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: string };
              return (
                <li key={key} {...rest}>
                  <Checkbox size="small" checked={isSelected} sx={{ mr: 1, p: 0.5 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      {option.label}
                    </Typography>
                    {option.hint ? (
                      <Typography variant="caption" color="text.secondary" noWrap component="div">
                        {option.hint}
                      </Typography>
                    ) : null}
                  </Box>
                </li>
              );
            }}
            renderInput={(inputParams) => (
              <TextField
                {...inputParams}
                label={f.label}
                placeholder={
                  locked
                    ? `Elige ${requiredLabel} primero`
                    : selected.length
                      ? undefined
                      : "Buscar"
                }
              />
            )}
            noOptionsText="Sin opciones en el período"
          />
        );
      })}
    </Box>
  );
};

export default ExportFiltersSection;
